import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useWeb3 } from '../context/Web3Context';
import { 
  TrendingUp, TrendingDown, DollarSign, Droplets, ExternalLink,
  RefreshCw, AlertTriangle, ArrowRightLeft, Info, ArrowLeft
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MarketPrices = () => {
  const { account, connectWallet, isConnecting, contractAddresses } = useWeb3();
  
  const [prices, setPrices] = useState({
    eth: { usd: 0, change24h: 0 },
    beth: { usd: 1.00, note: '' }
  });
  const [loading, setLoading] = useState(true);
  const [poolExists, setPoolExists] = useState(false);

  useEffect(() => {
    fetchPrices();
    const interval = setInterval(fetchPrices, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);

  const fetchPrices = async () => {
    try {
      const response = await fetch(`${API}/market/prices`);
      const data = await response.json();
      setPrices(data);
      
      // Check if Uniswap pool exists (would have different BETH price)
      setPoolExists(data.beth?.usd !== 1.00);
    } catch (error) {
      console.error('Error fetching prices:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatUSD = (num) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  const formatPercent = (num) => {
    const sign = num >= 0 ? '+' : '';
    return `${sign}${num.toFixed(2)}%`;
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="market-prices-page">
      {/* Header */}
      <div className="bg-gray-900/50 border-b border-gray-800 px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link to="/admin-dashboard">
              <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <TrendingUp className="w-6 h-6" />
                Market Prices
              </h1>
              <p className="text-gray-400 text-sm">Live cryptocurrency prices and BETH valuation</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={fetchPrices} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Price Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {/* ETH Price */}
          <Card className="bg-gradient-to-br from-blue-900/40 to-blue-800/20 border-blue-500/30">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <span className="text-2xl">Ξ</span>
                  </div>
                  <div>
                    <p className="text-white font-bold text-lg">Ethereum</p>
                    <p className="text-gray-400 text-sm">ETH</p>
                  </div>
                </div>
                <a 
                  href="https://www.coingecko.com/en/coins/ethereum"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-500 hover:text-white"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
              <p className="text-3xl font-bold text-white mb-2">
                {formatUSD(prices.eth?.usd || 0)}
              </p>
              <p className={`text-sm flex items-center gap-1 ${
                (prices.eth?.change24h || prices.eth?.change_24h || 0) >= 0 ? 'text-green-400' : 'text-red-400'
              }`}>
                {(prices.eth?.change24h || prices.eth?.change_24h || 0) >= 0 ? (
                  <TrendingUp className="w-4 h-4" />
                ) : (
                  <TrendingDown className="w-4 h-4" />
                )}
                {formatPercent(prices.eth?.change24h || prices.eth?.change_24h || 0)} (24h)
              </p>
            </CardContent>
          </Card>

          {/* BETH Price */}
          <Card className="bg-gradient-to-br from-yellow-900/40 to-yellow-800/20 border-yellow-500/30">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center">
                    <span className="text-2xl">₿</span>
                  </div>
                  <div>
                    <p className="text-white font-bold text-lg">Bethereum</p>
                    <p className="text-gray-400 text-sm">BETH</p>
                  </div>
                </div>
                <a 
                  href={`https://etherscan.io/token/${contractAddresses.BETH_TOKEN}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-500 hover:text-white"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
              <p className="text-3xl font-bold text-white mb-2">
                {formatUSD(prices.beth?.usd || 1.00)}
              </p>
              <p className="text-sm text-gray-400">
                {poolExists ? 'Uniswap market price' : 'Internal valuation'}
              </p>
            </CardContent>
          </Card>

          {/* BETH/ETH Ratio */}
          <Card className="bg-gradient-to-br from-purple-900/40 to-purple-800/20 border-purple-500/30">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <ArrowRightLeft className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-white font-bold text-lg">BETH/ETH</p>
                    <p className="text-gray-400 text-sm">Exchange Rate</p>
                  </div>
                </div>
              </div>
              <p className="text-3xl font-bold text-white mb-2">
                {((prices.beth?.usd || 1) / (prices.eth?.usd || 3500)).toFixed(8)}
              </p>
              <p className="text-sm text-gray-400">
                1 BETH = {((prices.beth?.usd || 1) / (prices.eth?.usd || 3500)).toFixed(6)} ETH
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Liquidity Pool Status */}
        <Card className="mb-8 bg-green-900/20 border-green-500/30">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              BETH Pricing Model
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-green-400">
                <div className="w-3 h-3 rounded-full bg-green-400 animate-pulse"></div>
                Internal Pricing Active - Platform Fully Operational
              </div>
              <p className="text-gray-400 text-sm">
                BETH uses <strong className="text-white">internal treasury pricing</strong> at <strong className="text-green-400">${prices.beth?.usd || '1.00'}</strong> per token. 
                This is the authoritative price source for all platform operations including collateral valuation and credit enhancement.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-800/50 p-3 rounded">
                  <p className="text-xs text-gray-500">Pricing Source</p>
                  <p className="text-white font-bold">Internal Treasury</p>
                </div>
                <div className="bg-gray-800/50 p-3 rounded">
                  <p className="text-xs text-gray-500">Price Stability</p>
                  <p className="text-green-400 font-bold">Stable</p>
                </div>
                <div className="bg-gray-800/50 p-3 rounded">
                  <p className="text-xs text-gray-500">DEX Required</p>
                  <p className="text-white font-bold">No</p>
                </div>
                <div className="bg-gray-800/50 p-3 rounded">
                  <p className="text-xs text-gray-500">Admin Adjustable</p>
                  <p className="text-white font-bold">Yes</p>
                </div>
              </div>
              <div className="p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-400 mt-0.5" />
                  <div>
                    <p className="text-white font-medium mb-1">Private Credit Infrastructure</p>
                    <p className="text-sm text-gray-400">
                      As a private credit platform, BETH pricing is managed internally. This provides price stability 
                      and compliance-safe operations without dependence on external DEX liquidity. 
                      Uniswap pool creation is optional and can be added later for reference pricing if needed.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Token Info */}
        <Card className="bg-gray-900/50 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white">BETH Token Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-gray-800/50 p-4 rounded">
                <p className="text-xs text-gray-500 mb-1">Contract Address</p>
                <a
                  href={`https://etherscan.io/token/${contractAddresses.BETH_TOKEN}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:text-blue-300 text-sm font-mono flex items-center gap-1"
                >
                  {contractAddresses.BETH_TOKEN?.slice(0, 10)}...
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
              <div className="bg-gray-800/50 p-4 rounded">
                <p className="text-xs text-gray-500 mb-1">Total Supply</p>
                <p className="text-white font-bold">1,000,000,000,000</p>
                <p className="text-xs text-gray-500">1 Trillion BETH</p>
              </div>
              <div className="bg-gray-800/50 p-4 rounded">
                <p className="text-xs text-gray-500 mb-1">Market Cap</p>
                <p className="text-white font-bold">
                  {formatUSD(1000000000000 * (prices.beth?.usd || 1))}
                </p>
                <p className="text-xs text-gray-500">Fully diluted</p>
              </div>
              <div className="bg-gray-800/50 p-4 rounded">
                <p className="text-xs text-gray-500 mb-1">Network</p>
                <p className="text-white font-bold">Ethereum Mainnet</p>
                <p className="text-xs text-gray-500">Chain ID: 1</p>
              </div>
            </div>

            <div className="mt-6 p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-400 mt-0.5" />
                <div>
                  <p className="text-white font-medium mb-1">Add BETH to MetaMask</p>
                  <p className="text-sm text-gray-400 mb-3">
                    Import the token to see your balance in MetaMask wallet.
                  </p>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={async () => {
                      if (window.ethereum) {
                        try {
                          await window.ethereum.request({
                            method: 'wallet_watchAsset',
                            params: {
                              type: 'ERC20',
                              options: {
                                address: contractAddresses.BETH_TOKEN,
                                symbol: 'BETH',
                                decimals: 18,
                              },
                            },
                          });
                          toast.success('BETH token added to MetaMask!');
                        } catch (error) {
                          toast.error('Failed to add token');
                        }
                      } else {
                        toast.error('MetaMask not found');
                      }
                    }}
                  >
                    Add to MetaMask
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MarketPrices;
